import React from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import * as Api from 'src/api'
import { CommonError } from 'src/api/base'
import PortfolioIcon from 'src/assets/icons/customIcons/menu-icons/Portfolio'
import { Plus } from 'src/assets/icons/customIcons/Plus'
import { errorMessage } from 'src/helpers/fns'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import Warning from 'src/imgs/classroom_icon.svg'
import { Button } from 'src/tailwind/components/Button'
import { Section } from 'src/tailwind/components/Section'
import * as Table from 'src/tailwind/components/Table'
import NoContent from 'src/views/components/NoContent'
import PortfolioSemesterSelect from 'src/views/includes/PortfolioSemesterSelect'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'
import { getStatusColor, getStatusLabel } from '../portfolioUtils'

export default function AdministratorPortfolioStudents() {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('student:students')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element | null {
  const headers = useAuthenticatedHeaders()
  const locale = useLocale()
  const t = useTranslatable()
  const canManagePortfolio = useAdminAccess().canManage('portfolio')
  const activeSemester = useActiveSemester()
  const [searchParams] = useSearchParams({
    semId: activeSemester?.id as string,
  })
  const semId = searchParams.get('semId')

  const { data, error } = useApi({
    endpoint: Api.getAdministratorPortfolioStudents,
    params: React.useMemo(
      () => ({
        headers,
        query: {
          semId: semId!,
        },
      }),
      [headers, semId]
    ),
    shouldFetch: semId != null,
  })

  const breadcrumbsItems = [
    {
      page: `${t('portfolio:portfolio')} - ${t('student:students')}`,
      path: `/${locale}/administrator/portfolio/students`,
    },
  ]

  const hasData = data != null && data.length > 0

  return (
    <Section
      title={t('student:students')}
      breadcrubms={breadcrumbsItems}
      icon={<PortfolioIcon />}
      rightElement={
        canManagePortfolio ? (
          <Link to={`/${locale}/administrator/portfolio/students/add`}>
            <Button variant="blue" className="flex items-center">
              <Plus color="white" /> <span className="ml-2">{t('common:add')}</span>
            </Button>
          </Link>
        ) : undefined
      }
    >
      <PortfolioSemesterSelect />

      {error != null ? (
        <NoContent
          header={t('error:an_error_occurred')}
          image={Warning}
          subHeader={errorMessage(error as unknown as CommonError)}
        />
      ) : !hasData ? (
        <NoContent
          header={t('error:records_not_found')}
          image={Warning}
          subHeader={t(['სტუდენტები არ მოიძებნა', 'No students assigned'])}
        />
      ) : (
        <Table.Table className="mt-7 w-full">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('common:unique')}</Table.Th>
              <Table.Th>{t('portfolio:mentor')}</Table.Th>
              <Table.Th>{t('student:student')}</Table.Th>
              <Table.Th>{t('portfolio:section')}</Table.Th>
              <Table.Th>{t('common:semester')}</Table.Th>
              <Table.Th>{t('common:status')}</Table.Th>
              <Table.Th>{t('common:created_at')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.map((item) => (
              <Table.Tr key={item.id}>
                <Table.Td>{item.id}</Table.Td>
                <Table.Td>{`${item.mentor?.name} (${item.mentor?.persNum})`}</Table.Td>
                <Table.Td>{`${item.student?.name} (${item.student?.persNum})`}</Table.Td>
                <Table.Td>{item.section?.name}</Table.Td>
                <Table.Td>{item.semester}</Table.Td>
                <Table.Td>
                  <span className={getStatusColor(item.status)}>{getStatusLabel(t, item.status.value)}</span>
                </Table.Td>
                <Table.Td className="whitespace-nowrap">{item.createdAt}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table.Table>
      )}
    </Section>
  )
}
